import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Leaf, Users, MapPin, Calendar, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { calculateCarbonFootprint, TransportMode, transportModeLabels } from "@/utils/carbonCalculations";
import EmailCaptureModal from "@/components/calculators/EmailCaptureModal";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { getInitialUrl, getStoredUTMParams } from "@/lib/analytics";
import { getCampaignName } from "@/lib/campaignMapping";
import { toast } from "sonner";
const COLORS = ["#22C55E", "#3B82F6", "#F59E0B"];
const CarbonEstimatorSection = () => {
  const [participants, setParticipants] = useState(20);
  const [distance, setDistance] = useState(100);
  const [days, setDays] = useState(2);
  const [mealsPerDay, setMealsPerDay] = useState(3);
  const [transportMode, setTransportMode] = useState<TransportMode>("train");
  const [vegetarianPercentage, setVegetarianPercentage] = useState(20);
  const [withAccommodation, setWithAccommodation] = useState(true);
  const [isResultsRevealed, setIsResultsRevealed] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const breakdown = useMemo(() => {
    return calculateCarbonFootprint({
      participants,
      distance,
      days,
      mealsPerDay,
      transportMode,
      vegetarianPercentage,
      withAccommodation
    });
  }, [participants, distance, days, mealsPerDay, transportMode, vegetarianPercentage, withAccommodation]);
  const chartData = useMemo(() => {
    const data = [];
    if (breakdown.transport > 0) data.push({
      name: "Transport",
      value: breakdown.transport
    });
    if (breakdown.meals > 0) data.push({
      name: "Repas",
      value: breakdown.meals
    });
    if (breakdown.accommodation > 0) data.push({
      name: "Hébergement",
      value: breakdown.accommodation
    });
    return data;
  }, [breakdown]);
  const handleEmailSubmit = async (email: string) => {
    setIsLoading(true);
    try {
      const slackChannel = email === "test@naboo.app" ? "C09TA10FMU5" : "C08TMC9AYLR";
      const initialUrl = getInitialUrl();
      const linkWithBrokenProtocol = initialUrl.replace("https://", "https ://").replace("http://", "http ://");

      // Get UTM parameters and campaign name
      const storedUTMs = getStoredUTMParams();
      const urlParams = new URLSearchParams(new URL(initialUrl).search);
      const campaignName = getCampaignName(urlParams, storedUTMs);
      const utmSource = storedUTMs?.utm_source || urlParams.get('utm_source') || '';
      const utmMedium = storedUTMs?.utm_medium || urlParams.get('utm_medium') || '';
      const utmContent = storedUTMs?.utm_content || urlParams.get('utm_content') || '';
      const message = `🌿 *Nouveau calcul carbone*
*Email* : ${email}
*Market* : FR
*Participants* : ${participants}
*Distance* : ${distance} km
*Transport* : ${transportModeLabels[transportMode]}
*Jours* : ${days}
*Repas/jour* : ${mealsPerDay}
*Végétarien* : ${vegetarianPercentage}%
*Hébergement* : ${withAccommodation ? "Oui" : "Non"}
*Empreinte totale* : ${breakdown.total.toLocaleString("fr-FR")} kg CO₂
*Par personne* : ${breakdown.perPerson} kg CO₂
*Lien* : ${linkWithBrokenProtocol}
${campaignName ? `*Campagne* : ${campaignName}` : ''}
${utmSource ? `*Source* : ${utmSource}` : ''}
${utmMedium ? `*Medium* : ${utmMedium}` : ''}
${utmContent ? `*Contenu* : ${utmContent}` : ''}`;
      await sendSlackMessage(message, slackChannel);
      setShowEmailModal(false);
      setIsResultsRevealed(true);
      toast.success("Votre impact carbone a été calculé avec succès !");
    } catch (error) {
      console.error("Erreur lors de l'envoi Slack:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };
  return <section className="py-16 md:py-24 px-4">
      <div className="container mx-auto max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12">
          <div className="inline-flex items-center bg-[#C6E278] text-black px-4 py-2 rounded-full text-sm font-normal mb-4">
            <Leaf className="w-4 h-4 mr-2" />
            Outil gratuit
          </div>
          <h2 className="text-3xl md:text-4xl text-foreground mb-4 font-normal">
            Calculateur Carbone
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Calculez l'impact environnemental de votre événement d'entreprise
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="space-y-8 bg-white rounded-2xl p-8 shadow-sm">
            {/* Participants */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Nombre de participants
                </Label>
                <span className="font-semibold text-green-600">{participants}</span>
              </div>
              <Slider value={[participants]} onValueChange={(value) => setParticipants(value[0])} min={5} max={100} step={5} className="w-full" />
            </div>

            {/* Distance */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Distance (km)
                </Label>
                <span className="font-semibold text-green-600">{distance} km</span>
              </div>
              <Slider value={[distance]} onValueChange={(value) => setDistance(value[0])} min={10} max={500} step={10} className="w-full" />
            </div>

            {/* Days */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Nombre de jours
                </Label>
                <span className="font-semibold text-green-600">{days}</span>
              </div>
              <Slider value={[days]} onValueChange={(value) => setDays(value[0])} min={1} max={5} step={1} className="w-full" />
            </div>

            {/* Meals per day */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal flex items-center gap-2">
                  <Utensils className="w-4 h-4" />
                  Repas par jour
                </Label>
                <span className="font-semibold text-green-600">{mealsPerDay}</span>
              </div>
              <Slider value={[mealsPerDay]} onValueChange={(value) => setMealsPerDay(value[0])} min={1} max={4} step={1} className="w-full" />
            </div>

            {/* Transport Mode */}
            <div className="space-y-3">
              <Label className="text-base font-normal">Mode de transport</Label>
              <Select value={transportMode} onValueChange={(value) => setTransportMode(value as TransportMode)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(transportModeLabels).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Vegetarian Percentage */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal">Repas végétariens</Label>
                <span className="font-semibold text-green-600">{vegetarianPercentage}%</span>
              </div>
              <Slider value={[vegetarianPercentage]} onValueChange={(value) => setVegetarianPercentage(value[0])} min={0} max={100} step={10} className="w-full" />
            </div>

            {/* Accommodation */}
            <div className="flex items-center justify-between">
              <Label className="text-base font-normal">Hébergement inclus</Label>
              <Switch checked={withAccommodation} onCheckedChange={setWithAccommodation} />
            </div>

            {/* CTA Button */}
            <Button onClick={() => setShowEmailModal(true)} className="w-full bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:scale-105 hover:shadow-xl hover:shadow-cta-green/30 rounded-md transition-all duration-300 py-6 text-sm" size="lg">
              <Leaf className="w-5 h-5 mr-2" />
              Calculer l'impact
            </Button>
          </motion.div>

          {/* Results */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-2xl p-8 shadow-sm relative">
            {/* Blurred content */}
            <div className={`transition-all duration-500 ${!isResultsRevealed ? 'blur-md pointer-events-none select-none' : ''}`}>
              <div className="text-center mb-8">
                <p className="text-sm text-muted-foreground mb-2">Empreinte carbone totale</p>
                <div className="flex items-center justify-center gap-2">
                  <Leaf className="w-8 h-8 text-green-600" />
                  <span className="text-4xl md:text-5xl font-bold text-foreground">
                    {breakdown.total.toLocaleString("fr-FR")}
                  </span>
                  <span className="text-xl text-muted-foreground">kg CO₂</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {breakdown.perPerson} kg CO₂ par personne
                </p>
              </div>

              {chartData.length > 0 && <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                        {chartData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value: number) => `${value.toLocaleString("fr-FR")} kg CO₂`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>}

              <div className="mt-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Transport</span>
                  <span className="font-normal">{breakdown.transport.toLocaleString("fr-FR")} kg CO₂</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Repas</span>
                  <span className="font-normal">{breakdown.meals.toLocaleString("fr-FR")} kg CO₂</span>
                </div>
                {withAccommodation && <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Hébergement</span>
                    <span className="font-normal">{breakdown.accommodation.toLocaleString("fr-FR")} kg CO₂</span>
                  </div>}
              </div>

              <div className="mt-8 p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-green-800">
                  💡 <strong>Conseil :</strong> Optez pour le train et augmentez la part de repas végétariens pour réduire significativement l'empreinte carbone de votre événement.
                </p>
              </div>
            </div>

            {/* Email Capture Modal Overlay */}
            <EmailCaptureModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} onSubmit={handleEmailSubmit} type="carbon" isLoading={isLoading} />
          </motion.div>
        </div>
      </div>
    </section>;
};
export default CarbonEstimatorSection;